// src/pages/CreateGoal.tsx
// Step 4 of 5 - Goal selection
// IMPORTANT: Navigates to /verify-phone (NOT /payment)
// Styled to match app dashboard

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { currentUser } from "@/lib/supabase";
import { markCompleted } from "@/lib/onboarding";
import { ENV } from "@/lib/env";

const GOALS = [
  { 
    value: "Lose Weight", 
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6" />
      </svg>
    ),
    color: "from-orange-500 to-red-500",
    bgColor: "bg-orange-500/10",
    borderColor: "border-orange-500/30",
    desc: "Burn fat & get leaner"
  },
  { 
    value: "Gain Muscle", 
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
      </svg>
    ),
    color: "from-blue-500 to-indigo-500",
    bgColor: "bg-blue-500/10",
    borderColor: "border-blue-500/30",
    desc: "Build strength & size"
  },
  { 
    value: "Improve Performance", 
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
      </svg>
    ),
    color: "from-[#a78bfa] to-emerald-500",
    bgColor: "bg-[#a78bfa]/10",
    borderColor: "border-[#a78bfa]/30",
    desc: "Optimise for your sport"
  },
];

function StepIndicator({ current, total }: { current: number; total: number }) {
  return (
    <div className="flex items-center justify-center gap-2">
      {Array.from({ length: total }, (_, i) => (
        <div
          key={i}
          className={`h-1.5 rounded-full transition-all duration-500 ${
            i < current 
              ? "w-8 bg-[#a78bfa]" 
              : i === current 
                ? "w-8 bg-[#a78bfa]/40" 
                : "w-3 bg-[#f1f5f9]"
          }`}
        />
      ))}
    </div>
  );
}

export default function CreateGoal() {
  const navigate = useNavigate();
  
  const [goalType, setGoalType] = useState("");
  const [goalDescription, setGoalDescription] = useState("");
  const [targetWeight, setTargetWeight] = useState("");
  const [goalDate, setGoalDate] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [showDetails, setShowDetails] = useState(false);

  const handleGoalSelect = (goal: string) => {
    setGoalType(goal);
    setTimeout(() => setShowDetails(true), 100);
  };

  const handleNext = async () => {
    setSaving(true);
    setErr(null);

    try {
      const user = await currentUser();
      if (!user) throw new Error("Not logged in");
      if (!goalType) throw new Error("Please select a goal");

      const payload = {
        user_id: user.id,
        goal_type: goalType,
        client_goal_description: goalDescription.trim() || null,
        client_weight_goal_in_kg: targetWeight ? Number(targetWeight) : null,
        target_date: goalDate || null,
      };

      const resp = await fetch(`${ENV.EDGE_BASE}/new-client-goal-create`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await resp.json();
      if (!resp.ok) throw new Error(data.error || "Failed to create goal");

      markCompleted("createGoal");
      
      // ✅ CORRECT: Navigate to verify-phone BEFORE payment
      navigate("/verify-phone");
    } catch (e: any) {
      setErr(e.message || "Something went wrong");
    } finally {
      setSaving(false);
    }
  };

  const today = new Date().toISOString().split("T")[0];

  return (
    <div className="min-h-screen bg-[#ffffff] text-[#0f172a]">
      {/* Header */}
      <header className="flex items-center justify-between px-6 py-4 border-b border-[#e2e8f0]">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-1.5 text-gray-500 hover:text-[#0f172a] transition-colors"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          <span className="text-sm">Back</span>
        </button>
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-[#a78bfa] flex items-center justify-center">
            <svg className="w-5 h-5 text-[#0f172a]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M13 10V3L4 14h7v7l9-11h-7z" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <span className="font-semibold text-[#0f172a]">Input</span>
        </div>
        <div className="w-16"></div>
      </header>

      {/* Main Content */}
      <main className="flex flex-col items-center px-4 py-6">
        <div className="w-full max-w-md">
          
          {/* Title */}
          <div className="text-center mb-6">
            <h1 className="text-2xl font-bold text-[#0f172a] mb-2">
              What's your goal?
            </h1>
            <p className="text-gray-500 text-sm">
              This helps us calculate your nutrition targets
            </p>
          </div>

          {/* Goal Cards */}
          <div className="space-y-3 mb-6">
            {GOALS.map((goal) => {
              const isSelected = goalType === goal.value;
              
              return (
                <button
                  key={goal.value}
                  onClick={() => handleGoalSelect(goal.value)}
                  className={`w-full p-4 rounded-2xl border-2 transition-all duration-300 flex items-center gap-4 ${
                    isSelected
                      ? `${goal.bgColor} ${goal.borderColor}`
                      : "bg-[#f8fafc] border-[#e2e8f0] hover:border-[#e2e8f0]"
                  }`}
                >
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${goal.color} flex items-center justify-center flex-shrink-0 transition-transform duration-300 ${isSelected ? "scale-110" : ""}`}>
                    <span className="text-[#0f172a]">{goal.icon}</span>
                  </div>
                  <div className="text-left flex-1">
                    <p className={`font-semibold text-base transition-colors ${isSelected ? "text-[#0f172a]" : "text-gray-600"}`}>
                      {goal.value}
                    </p>
                    <p className="text-sm text-gray-500">{goal.desc}</p>
                  </div>
                  <div className={`w-6 h-6 rounded-full border-2 transition-all duration-300 flex items-center justify-center ${
                    isSelected 
                      ? "border-[#a78bfa] bg-[#a78bfa]" 
                      : "border-[#e2e8f0]"
                  }`}>
                    {isSelected && (
                      <svg className="w-3.5 h-3.5 text-[#0f172a]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </div>
                </button>
              );
            })}
          </div>

          {/* Optional Details Card */}
          {goalType && showDetails && (
            <div className="bg-[#f8fafc] border border-[#e2e8f0] rounded-2xl p-5 mb-6 animate-in fade-in slide-in-from-top-2 duration-300">
              <p className="text-sm text-gray-500 text-center mb-4">
                Optional — add more detail
              </p>
              
              {/* Description */}
              <div className="mb-4">
                <input
                  type="text"
                  value={goalDescription}
                  onChange={(e) => setGoalDescription(e.target.value)}
                  placeholder={
                    goalType === "Lose Weight" 
                      ? "e.g., Drop body fat while keeping muscle"
                      : goalType === "Gain Muscle"
                      ? "e.g., Add 5kg of lean mass"
                      : "e.g., Improve my 5K time"
                  }
                  className="w-full h-12 px-4 bg-[#f1f5f9] border border-[#e2e8f0] rounded-xl text-[#0f172a] placeholder-gray-600 focus:outline-none focus:border-[#a78bfa]/50 transition-all text-sm"
                />
              </div>

              {/* Target Weight + Date */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <input
                    type="number"
                    value={targetWeight}
                    onChange={(e) => setTargetWeight(e.target.value)}
                    placeholder="Target kg"
                    className="w-full h-12 px-4 bg-[#f1f5f9] border border-[#e2e8f0] rounded-xl text-[#0f172a] placeholder-gray-600 focus:outline-none focus:border-[#a78bfa]/50 transition-all"
                  />
                </div>
                <div>
                  <input
                    type="date"
                    value={goalDate}
                    onChange={(e) => setGoalDate(e.target.value)}
                    min={today}
                    className="w-full h-12 px-3 bg-[#f1f5f9] border border-[#e2e8f0] rounded-xl text-[#0f172a] focus:outline-none focus:border-[#a78bfa]/50 transition-all [color-scheme:dark]"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Continue Button */}
          <button
            onClick={handleNext}
            disabled={saving || !goalType}
            className="w-full h-12 bg-[#a78bfa] hover:bg-[#a78bfa]/90 disabled:bg-[#f1f5f9] disabled:text-gray-600 text-[#0f172a] font-semibold text-base rounded-xl transition-all duration-200 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {saving ? (
              <>
                <svg className="w-5 h-5 animate-spin" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Creating plan...
              </>
            ) : "Continue"}
          </button>

          {/* Error */}
          {err && (
            <div className="mt-4 p-3 bg-red-500/10 border border-red-500/20 rounded-xl">
              <p className="text-red-400 text-sm text-center">{err}</p>
            </div>
          )}

          {/* Step Indicator */}
          <div className="mt-10">
            <StepIndicator current={3} total={5} />
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-4 text-center border-t border-[#e2e8f0]">
        <p className="text-gray-600 text-xs">
          © {new Date().getFullYear()} Input
        </p>
      </footer>
    </div>
  );
}
